import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Brain, Users, TrendingUp, TrendingDown, AlertTriangle, 
  Search, RefreshCw, ChevronRight, Target, Activity,
  BookOpen, Clock, UserX, Award, Zap, BarChart3,
  AlertCircle, CheckCircle, XCircle, Eye, FileText,
  Sparkles, GraduationCap, Heart, ArrowUp, ArrowDown,
  Filter, Download, Bell, ChevronDown, Loader2, Trash2, Settings
} from 'lucide-react';

// Cores para classificações
const CLASSIFICATION_COLORS = {
  excelente: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-500', badge: 'bg-green-500' },
  estavel: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500', badge: 'bg-blue-500' },
  atencao: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-500', badge: 'bg-yellow-500' },
  critico: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-500', badge: 'bg-red-500' }
};

const RISK_COLORS = {
  baixo: 'bg-green-500',
  medio: 'bg-yellow-500',
  alto: 'bg-orange-500',
  critico: 'bg-red-500'
};

// Componente de Score Circular
function ScoreCircle({ score, size = 'md', label }) {
  const sizeClasses = {
    sm: 'w-16 h-16 text-lg',
    md: 'w-24 h-24 text-2xl',
    lg: 'w-32 h-32 text-4xl'
  };
  
  const getColor = (s) => {
    if (s >= 80) return 'from-green-400 to-green-600';
    if (s >= 60) return 'from-blue-400 to-blue-600';
    if (s >= 40) return 'from-yellow-400 to-yellow-600';
    return 'from-red-400 to-red-600';
  };
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${getColor(score)} flex items-center justify-center shadow-lg`}>
        <span className="font-bold text-white">{Math.round(score)}</span>
      </div>
      {label && <span className="text-sm text-gray-600 font-medium">{label}</span>}
    </div>
  );
}

// Componente de Card de Métrica
function MetricCard({ title, value, subtitle, icon: Icon, color = 'purple', trend }) {
  const colorClasses = {
    purple: 'from-purple-500 to-purple-700',
    green: 'from-green-500 to-green-700',
    blue: 'from-blue-500 to-blue-700',
    red: 'from-red-500 to-red-700',
    yellow: 'from-yellow-500 to-yellow-700',
    orange: 'from-orange-500 to-orange-700'
  };
  
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className={`h-1 bg-gradient-to-r ${colorClasses[color]}`} />
      <CardContent className="pt-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} text-white`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            <span>{Math.abs(trend)}% vs mês anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Componente de Badge de Risco
function RiskBadge({ level }) {
  const labels = {
    baixo: 'Baixo',
    medio: 'Médio',
    alto: 'Alto',
    critico: 'Crítico'
  };
  
  return (
    <Badge className={`${RISK_COLORS[level]} text-white`}>
      {labels[level]}
    </Badge>
  );
}

// Componente de Status Visual
function StatusIndicator({ classificacao }) {
  const colors = CLASSIFICATION_COLORS[classificacao] || CLASSIFICATION_COLORS.estavel;
  const labels = {
    excelente: 'Excelente',
    estavel: 'Estável',
    atencao: 'Atenção',
    critico: 'Crítico'
  };
  
  return (
    <div className={`px-3 py-1 rounded-full ${colors.bg} ${colors.text} text-sm font-medium`}>
      {labels[classificacao]}
    </div>
  );
}

// Modal do Aluno
function StudentModal({ student, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  useEffect(() => {
    if (open && student?.id) {
      loadStudentData();
    }
  }, [open, student?.id]);
  
  const loadStudentData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dnacore/student/${student.id}`);
      setData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do aluno');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGeneratePlan = async () => {
    setGeneratingPlan(true);
    try {
      const res = await api.post(`/dnacore/student/${student.id}/plan`);
      setData(prev => ({ ...prev, plano_individual: res.data }));
      toast.success('Plano individual gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar plano');
    } finally {
      setGeneratingPlan(false);
    }
  };
  
  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post(`/dnacore/student/${student.id}/analyze`);
      setData(prev => ({
        ...prev,
        scores: res.data.scores,
        analise_ia: res.data.analise,
        sugestoes: res.data.sugestoes_intervencao
      }));
      toast.success('Análise atualizada!');
    } catch (error) {
      toast.error('Erro na análise');
    } finally {
      setAnalyzing(false);
    }
  };
  
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-xl font-bold">
              {student?.name?.charAt(0)}
            </div>
            <div>
              <div className="text-xl">{student?.name}</div>
              <div className="text-sm text-gray-500 font-normal">{student?.class_name} • Mat: {student?.registration}</div>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          {loading ? (
            <div className="space-y-4 p-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : data ? (
            <Tabs defaultValue="scores" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="scores">Scores</TabsTrigger>
                <TabsTrigger value="analise">Análise IA</TabsTrigger>
                <TabsTrigger value="historico">Histórico</TabsTrigger>
                <TabsTrigger value="plano">Plano Individual</TabsTrigger>
              </TabsList>
              
              {/* Tab Scores */}
              <TabsContent value="scores" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <ScoreCircle score={data.scores?.score_total || 0} size="lg" label="Score Total" />
                    <StatusIndicator classificacao={data.scores?.classificacao || 'estavel'} />
                  </div>
                  <Button onClick={handleAnalyze} disabled={analyzing} variant="outline">
                    {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Atualizar Análise
                  </Button>
                </div>
                
                <div className="grid grid-cols-5 gap-4 mt-6">
                  {[
                    { label: 'Acadêmico', value: data.scores?.score_academico, icon: BookOpen, color: 'text-blue-600' },
                    { label: 'Frequência', value: data.scores?.score_frequencia, icon: Clock, color: 'text-green-600' },
                    { label: 'Comportamento', value: data.scores?.score_comportamental, icon: Heart, color: 'text-purple-600' },
                    { label: 'Evolução', value: data.scores?.score_evolucao, icon: TrendingUp, color: 'text-orange-600' },
                    { label: 'Emocional', value: data.scores?.score_emocional, icon: Activity, color: 'text-pink-600' }
                  ].map((item, i) => (
                    <Card key={i} className="text-center p-4">
                      <item.icon className={`w-6 h-6 mx-auto ${item.color}`} />
                      <div className="text-2xl font-bold mt-2">{Math.round(item.value || 0)}</div>
                      <div className="text-xs text-gray-500">{item.label}</div>
                    </Card>
                  ))}
                </div>
                
                <Card className="p-4 mt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    Indicadores de Risco
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Risco de Queda</span>
                      <RiskBadge level={data.scores?.risco_queda || 'baixo'} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Risco de Reprovação</span>
                      <RiskBadge level={data.scores?.risco_reprovacao || 'baixo'} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Risco de Desengajamento</span>
                      <RiskBadge level={data.scores?.risco_desengajamento || 'baixo'} />
                    </div>
                  </div>
                </Card>
              </TabsContent>
              
              {/* Tab Análise IA */}
              <TabsContent value="analise" className="space-y-4 mt-4">
                {data.analise_ia?.error ? (
                  <Alert variant="destructive">
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription>{data.analise_ia.error}</AlertDescription>
                  </Alert>
                ) : data.analise_ia ? (
                  <>
                    <Card className="p-4 bg-gradient-to-r from-purple-50 to-blue-50">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-600" />
                        Resumo da IA
                      </h4>
                      <p className="text-gray-700">{data.analise_ia.resumo_geral}</p>
                    </Card>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="p-4">
                        <h4 className="font-semibold mb-2 text-green-600 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Pontos Fortes
                        </h4>
                        <ul className="space-y-1">
                          {data.analise_ia.pontos_fortes?.map((p, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-green-500 mt-1">•</span> {p}
                            </li>
                          ))}
                        </ul>
                      </Card>
                      
                      <Card className="p-4">
                        <h4 className="font-semibold mb-2 text-orange-600 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" /> Áreas de Atenção
                        </h4>
                        <ul className="space-y-1">
                          {data.analise_ia.areas_atencao?.map((a, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-orange-500 mt-1">•</span> {a}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    </div>
                    
                    <Card className="p-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-600" /> Previsão
                      </h4>
                      <p className="text-gray-700">{data.analise_ia.previsao_trimestre}</p>
                    </Card>
                    
                    <Card className="p-4 bg-purple-50 border-purple-200">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-purple-700">
                        <Sparkles className="w-4 h-4" /> Recomendação Principal
                      </h4>
                      <p className="text-purple-900">{data.analise_ia.recomendacao_principal}</p>
                    </Card>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Clique em "Atualizar Análise" para gerar insights com IA</p>
                  </div>
                )}
              </TabsContent>
              
              {/* Tab Histórico */}
              <TabsContent value="historico" className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card className="p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{data.historico?.taxa_presenca || 0}%</div>
                    <div className="text-sm text-gray-500">Taxa de Presença</div>
                  </Card>
                  <Card className="p-4 text-center">
                    <div className="text-3xl font-bold text-red-600">{data.historico?.faltas_total || 0}</div>
                    <div className="text-sm text-gray-500">Faltas</div>
                  </Card>
                  <Card className="p-4 text-center">
                    <div className="text-3xl font-bold text-orange-600">{data.historico?.ocorrencias?.length || 0}</div>
                    <div className="text-sm text-gray-500">Ocorrências</div>
                  </Card>
                </div>
                
                <Card className="p-4">
                  <h4 className="font-semibold mb-3">Notas por Período</h4>
                  {Object.entries(data.historico?.notas_por_periodo || {}).map(([periodo, notas]) => (
                    <div key={periodo} className="mb-4">
                      <h5 className="text-sm font-medium text-gray-600 mb-2">{periodo}</h5>
                      <div className="flex flex-wrap gap-2">
                        {notas.map((n, i) => (
                          <Badge key={i} variant="outline">
                            {n.disciplina}: {n.valor}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </Card>
              </TabsContent>
              
              {/* Tab Plano Individual */}
              <TabsContent value="plano" className="space-y-4 mt-4">
                {!data.plano_individual ? (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-500 mb-4">Nenhum plano individual gerado</p>
                    <Button onClick={handleGeneratePlan} disabled={generatingPlan}>
                      {generatingPlan ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Gerar Plano com IA
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-end">
                      <Button onClick={handleGeneratePlan} disabled={generatingPlan} variant="outline" size="sm">
                        {generatingPlan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Regenerar
                      </Button>
                    </div>
                    
                    {[
                      { title: 'Dificuldades Identificadas', data: data.plano_individual.dificuldades, icon: AlertTriangle, color: 'red' },
                      { title: 'Causas Prováveis', data: data.plano_individual.causas_provaveis, icon: Search, color: 'orange' },
                      { title: 'Ações Recomendadas', data: data.plano_individual.acoes_recomendadas, icon: Target, color: 'blue' },
                      { title: 'Metas Pedagógicas', data: data.plano_individual.metas_pedagogicas, icon: Award, color: 'green' },
                      { title: 'Estratégias de Ensino', data: data.plano_individual.estrategias_ensino, icon: BookOpen, color: 'purple' }
                    ].map((section, idx) => (
                      <Card key={idx} className="p-4">
                        <h4 className={`font-semibold mb-2 flex items-center gap-2 text-${section.color}-600`}>
                          <section.icon className="w-4 h-4" />
                          {section.title}
                        </h4>
                        <ul className="space-y-1">
                          {section.data?.map((item, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                              <ChevronRight className="w-4 h-4 mt-0.5 text-gray-400" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    ))}
                  </>
                )}
              </TabsContent>
            </Tabs>
          ) : null}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// Componente de Painel Global de Notas
function NotasGlobaisPanel({ turmas }) {
  const [loading, setLoading] = useState(true);
  const [notasData, setNotasData] = useState(null);
  const [alunosNotas, setAlunosNotas] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [tiposAvaliacao, setTiposAvaliacao] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [trimestresData, setTrimestresData] = useState(null);
  const [viewMode, setViewMode] = useState('geral'); // 'geral' ou 'trimestres'
  
  // Filtros
  const [filterPeriodo, setFilterPeriodo] = useState('all');
  const [filterTurma, setFilterTurma] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterDisciplina, setFilterDisciplina] = useState('all');
  const [filterProfessor, setFilterProfessor] = useState('all');
  const [showAbaixoMedia, setShowAbaixoMedia] = useState(false);
  
  // Estados para gerenciamento de notas
  const [showGerenciar, setShowGerenciar] = useState(false);
  const [professoresSelecionados, setProfessoresSelecionados] = useState([]);
  const [deletando, setDeletando] = useState(false);
  const [filterTurmaGerenciar, setFilterTurmaGerenciar] = useState('all');
  const [professoresFiltrados, setProfessoresFiltrados] = useState([]);
  
  // Estados para limpeza de dados de exemplo
  const [showLimpeza, setShowLimpeza] = useState(false);
  const [previewLimpeza, setPreviewLimpeza] = useState(null);
  const [limpando, setLimpando] = useState(false);
  
  useEffect(() => {
    loadNotasGlobal();
    loadPeriodos();
    loadTiposAvaliacao();
    loadDisciplinas();
    loadProfessores();
  }, []);
  
  useEffect(() => {
    loadNotasGlobal();
    loadAlunosNotas();
    loadTrimestres();
  }, [filterPeriodo, filterTurma, filterTipo, filterDisciplina, filterProfessor, showAbaixoMedia]);
  
  useEffect(() => {
    // Quando mudar a turma, recarregar disciplinas específicas
    loadDisciplinas();
  }, [filterTurma]);
  
  const loadNotasGlobal = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPeriodo !== 'all') params.periodo = filterPeriodo;
      if (filterTurma !== 'all') params.turma_id = filterTurma;
      if (filterTipo !== 'all') params.evaluation_type = filterTipo;
      if (filterDisciplina !== 'all') params.disciplina_id = filterDisciplina;
      if (filterProfessor !== 'all') params.professor_id = filterProfessor;
      
      const res = await api.get('/dnacore/notas/global', { params });
      setNotasData(res.data);
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadAlunosNotas = async () => {
    try {
      const params = { limit: 50, abaixo_media: showAbaixoMedia };
      if (filterPeriodo !== 'all') params.periodo = filterPeriodo;
      if (filterTurma !== 'all') params.turma_id = filterTurma;
      if (filterDisciplina !== 'all') params.disciplina_id = filterDisciplina;
      if (filterProfessor !== 'all') params.professor_id = filterProfessor;
      
      const res = await api.get('/dnacore/notas/alunos', { params });
      setAlunosNotas(res.data.alunos || []);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  };
  
  const loadProfessores = async (turmaId = null) => {
    try {
      const params = {};
      if (turmaId && turmaId !== 'all') params.turma_id = turmaId;
      
      const res = await api.get('/dnacore/notas/professores', { params });
      setProfessores(res.data.professores || []);
      
      // Atualizar também a lista filtrada para o gerenciador
      if (turmaId && turmaId !== 'all') {
        setProfessoresFiltrados(res.data.professores || []);
      } else {
        setProfessoresFiltrados(res.data.professores || []);
      }
    } catch (error) {
      console.error('Erro ao carregar professores:', error);
    }
  };
  
  const loadProfessoresGerenciar = async (turmaId = null) => {
    try {
      const params = {};
      if (turmaId && turmaId !== 'all') params.turma_id = turmaId;
      
      const res = await api.get('/dnacore/notas/professores', { params });
      setProfessoresFiltrados(res.data.professores || []);
    } catch (error) {
      console.error('Erro ao carregar professores filtrados:', error);
    }
  };
  
  const deletarNotasProfessor = async (professorId) => {
    setDeletando(true);
    try {
      const res = await api.delete(`/dnacore/notas/por-professor/${professorId}?confirmar=true`);
      toast.success(`${res.data.notas_deletadas} notas deletadas com sucesso!`);
      loadNotasGlobal();
      loadProfessores();
      loadAlunosNotas();
    } catch (error) {
      toast.error('Erro ao deletar notas');
    } finally {
      setDeletando(false);
    }
  };
  
  const deletarNotasSelecionadas = async () => {
    if (professoresSelecionados.length === 0) {
      toast.error('Selecione pelo menos um professor');
      return;
    }
    
    setDeletando(true);
    let totalDeletadas = 0;
    
    try {
      for (const profId of professoresSelecionados) {
        const res = await api.delete(`/dnacore/notas/por-professor/${profId}?confirmar=true`);
        totalDeletadas += res.data.notas_deletadas || 0;
      }
      
      toast.success(`${totalDeletadas} notas deletadas com sucesso!`);
      setProfessoresSelecionados([]);
      setShowGerenciar(false);
      loadNotasGlobal();
      loadProfessores();
      loadAlunosNotas();
    } catch (error) {
      toast.error('Erro ao deletar notas');
    } finally {
      setDeletando(false);
    }
  };
  
  const toggleProfessorSelecionado = (profId) => {
    setProfessoresSelecionados(prev => 
      prev.includes(profId) 
        ? prev.filter(id => id !== profId)
        : [...prev, profId]
    );
  };
  
  // Funções de limpeza de dados de exemplo
  const carregarPreviewLimpeza = async () => {
    try {
      const res = await api.get('/dnacore/limpar-dados-exemplo');
      setPreviewLimpeza(res.data);
    } catch (error) {
      toast.error('Erro ao carregar preview de limpeza');
    }
  };
  
  const executarLimpeza = async () => {
    if (!window.confirm('⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!\n\nTodos os dados de exemplo serão removidos permanentemente.\n\nDeseja continuar?')) {
      return;
    }
    
    setLimpando(true);
    try {
      const res = await api.delete('/dnacore/limpar-dados-exemplo?confirmar=true');
      toast.success(`Limpeza concluída! Removidos: ${res.data.removidos?.total || 0} registros`);
      setShowLimpeza(false);
      setPreviewLimpeza(null);
      // Recarregar dados
      loadNotasGlobal();
      loadProfessores();
      loadAlunosNotas();
    } catch (error) {
      toast.error('Erro ao executar limpeza');
    } finally {
      setLimpando(false);
    }
  };
  
  const loadDisciplinas = async () => {
    try {
      const params = {};
      if (filterTurma !== 'all') params.turma_id = filterTurma;
      
      const res = await api.get('/dnacore/disciplinas', { params });
      setDisciplinas(res.data.disciplinas || []);
    } catch (error) {
      console.error('Erro ao carregar disciplinas:', error);
    }
  };
  
  const loadTrimestres = async () => {
    try {
      const params = {};
      if (filterTurma !== 'all') params.turma_id = filterTurma;
      if (filterDisciplina !== 'all') params.disciplina_id = filterDisciplina;
      
      const res = await api.get('/dnacore/notas/trimestres', { params });
      setTrimestresData(res.data);
    } catch (error) {
      console.error('Erro ao carregar trimestres:', error);
    }
  };
  
  const loadPeriodos = async () => {
    try {
      const res = await api.get('/dnacore/notas/periodos');
      setPeriodos(res.data.periodos || []);
    } catch (error) {
      console.error('Erro ao carregar períodos:', error);
    }
  };
  
  const loadTiposAvaliacao = async () => {
    try {
      const res = await api.get('/dnacore/notas/tipos-avaliacao');
      setTiposAvaliacao(res.data.tipos || []);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  };
  
  const getNotaColor = (nota) => {
    if (nota >= 8) return 'bg-green-500';
    if (nota >= 6) return 'bg-blue-500';
    if (nota >= 4) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  if (loading && !notasData) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-purple-500 to-purple-700" />
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Total de Notas</p>
                <p className="text-3xl font-bold">{notasData?.resumo?.total_notas || 0}</p>
                <p className="text-xs text-gray-400">lançadas em {notasData?.resumo?.ano}</p>
              </div>
              <div className="p-3 rounded-xl bg-purple-100">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-700" />
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Média Geral</p>
                <p className="text-3xl font-bold">{notasData?.resumo?.media_geral?.toFixed(1) || '0'}</p>
                <p className="text-xs text-gray-400">de 0 a 10</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-100">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-green-500 to-green-700" />
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Notas Acima de 6</p>
                <p className="text-3xl font-bold text-green-600">
                  {((notasData?.distribuicao?.['6-8'] || 0) + (notasData?.distribuicao?.['8-10'] || 0))}
                </p>
                <p className="text-xs text-gray-400">aprovados</p>
              </div>
              <div className="p-3 rounded-xl bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-red-500 to-red-700" />
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Notas Abaixo de 6</p>
                <p className="text-3xl font-bold text-red-600">
                  {((notasData?.distribuicao?.['0-2'] || 0) + (notasData?.distribuicao?.['2-4'] || 0) + (notasData?.distribuicao?.['4-6'] || 0))}
                </p>
                <p className="text-xs text-gray-400">precisam atenção</p>
              </div>
              <div className="p-3 rounded-xl bg-red-100">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-purple-600" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={filterPeriodo} onValueChange={setFilterPeriodo}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Período/Trimestre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Períodos</SelectItem>
                {periodos.map((p) => (
                  <SelectItem key={p.periodo} value={p.periodo}>
                    {p.periodo} ({p.total_notas} notas)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterTurma} onValueChange={setFilterTurma}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Turma" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Turmas</SelectItem>
                {turmas.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tipo de Avaliação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                {tiposAvaliacao.map((t) => (
                  <SelectItem key={t.tipo} value={t.tipo}>
                    {t.tipo} ({t.total})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterDisciplina} onValueChange={setFilterDisciplina}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Disciplina" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Disciplinas</SelectItem>
                {disciplinas.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterProfessor} onValueChange={setFilterProfessor}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Professor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Professores</SelectItem>
                {professores.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nome} ({p.total_notas} notas)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant={showAbaixoMedia ? "default" : "outline"}
              onClick={() => setShowAbaixoMedia(!showAbaixoMedia)}
              className={showAbaixoMedia ? "bg-red-500 hover:bg-red-600" : ""}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {showAbaixoMedia ? "Mostrando Abaixo da Média" : "Filtrar Abaixo da Média"}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowGerenciar(true)}
              className="border-orange-300 text-orange-600 hover:bg-orange-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Gerenciar Notas
            </Button>
            
            <Button
              variant="outline"
              onClick={() => {
                setShowLimpeza(true);
                carregarPreviewLimpeza();
              }}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Limpar Dados de Exemplo
            </Button>
          </div>
          
          {/* Toggle de Visualização */}
          <div className="flex gap-2 mt-4 pt-4 border-t">
            <Button
              variant={viewMode === 'geral' ? "default" : "outline"}
              onClick={() => setViewMode('geral')}
              size="sm"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Visão Geral
            </Button>
            <Button
              variant={viewMode === 'trimestres' ? "default" : "outline"}
              onClick={() => setViewMode('trimestres')}
              size="sm"
            >
              <Clock className="w-4 h-4 mr-2" />
              Por Trimestre
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Visão por Trimestres */}
      {viewMode === 'trimestres' && trimestresData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              Médias por Trimestre
              {trimestresData.media_final && (
                <Badge className="ml-2 bg-green-500">
                  Média Final: {trimestresData.media_final}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {trimestresData.trimestres?.map((trim) => (
                <Card key={trim.trimestre} className={`p-4 ${trim.media_trimestre !== null ? 'bg-white' : 'bg-gray-50'}`}>
                  <h4 className="font-semibold text-lg mb-3">{trim.trimestre}</h4>
                  
                  <div className="space-y-3">
                    {/* Média do Trimestre */}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Média Trimestral</span>
                      {trim.media_trimestre !== null ? (
                        <div className={`px-3 py-1 rounded-full text-white font-bold ${getNotaColor(trim.media_trimestre)}`}>
                          {trim.media_trimestre}
                        </div>
                      ) : (
                        <Badge variant="secondary">Pendente</Badge>
                      )}
                    </div>
                    
                    {/* Recuperação */}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Recuperação</span>
                      {trim.media_recuperacao !== null ? (
                        <div className={`px-3 py-1 rounded-full text-white font-bold ${getNotaColor(trim.media_recuperacao)}`}>
                          {trim.media_recuperacao}
                        </div>
                      ) : (
                        <Badge variant="outline">-</Badge>
                      )}
                    </div>
                    
                    {/* Total de Notas */}
                    <div className="text-sm text-gray-500 pt-2 border-t">
                      {trim.total_notas} notas lançadas
                      {trim.total_recuperacao > 0 && ` | ${trim.total_recuperacao} recuperações`}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            
            {/* Média Final */}
            {!trimestresData.media_final_calculavel && (
              <Alert className="mt-4">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  A média final será calculada automaticamente após o lançamento das notas dos 3 trimestres.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Grid de Análises - só mostra na visão geral */}
      {viewMode === 'geral' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribuição de Notas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Distribuição de Notas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { range: '8-10', label: 'Excelente (8-10)', color: 'bg-green-500' },
                { range: '6-8', label: 'Bom (6-8)', color: 'bg-blue-500' },
                { range: '4-6', label: 'Regular (4-6)', color: 'bg-yellow-500' },
                { range: '2-4', label: 'Baixo (2-4)', color: 'bg-orange-500' },
                { range: '0-2', label: 'Crítico (0-2)', color: 'bg-red-500' }
              ].map((item) => {
                const count = notasData?.distribuicao?.[item.range] || 0;
                const total = notasData?.resumo?.total_notas || 1;
                const percent = (count / total * 100).toFixed(1);
                
                return (
                  <div key={item.range} className="flex items-center gap-3">
                    <div className="w-24 text-sm text-gray-600">{item.label}</div>
                    <div className="flex-1">
                      <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} transition-all duration-500`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-20 text-right">
                      <span className="font-semibold">{count}</span>
                      <span className="text-gray-400 text-sm ml-1">({percent}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        {/* Notas por Tipo de Avaliação */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              Média por Tipo de Avaliação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(notasData?.por_avaliacao || {}).map(([tipo, dados]) => (
                <div key={tipo} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{tipo}</span>
                    <span className="text-sm text-gray-500 ml-2">({dados.total} notas)</span>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-white font-semibold ${getNotaColor(dados.media)}`}>
                    {dados.media?.toFixed(1)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Notas por Período */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              Média por Período/Trimestre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(notasData?.por_periodo || {}).sort().map(([periodo, dados]) => (
                <div key={periodo} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{periodo}</span>
                    <span className="text-sm text-gray-500 ml-2">({dados.total} notas)</span>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-white font-semibold ${getNotaColor(dados.media)}`}>
                    {dados.media?.toFixed(1)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Ranking de Turmas por Notas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-purple-600" />
              Ranking de Turmas (por Notas)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {notasData?.por_turma?.map((turma, idx) => (
                  <div key={turma.turma_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-300 text-orange-900' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium truncate max-w-[150px]">{turma.turma_nome}</span>
                      <span className="text-xs text-gray-400">({turma.total_notas} notas)</span>
                    </div>
                    <div className={`px-2 py-1 rounded text-white text-sm font-bold ${getNotaColor(turma.media)}`}>
                      {turma.media?.toFixed(1)}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
      )}
      
      {/* Tabela de Alunos com Notas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            {showAbaixoMedia ? 'Alunos com Média Abaixo de 6' : 'Alunos e suas Médias'}
          </CardTitle>
          <CardDescription>
            {showAbaixoMedia 
              ? 'Alunos que precisam de atenção especial'
              : 'Visão geral das notas por aluno'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Aluno</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Turma</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Média</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total Notas</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Últimas Notas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {alunosNotas.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      Nenhum aluno encontrado com os filtros selecionados
                    </td>
                  </tr>
                ) : (
                  alunosNotas.map((aluno) => (
                    <tr key={aluno.student_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{aluno.name}</div>
                        <div className="text-xs text-gray-500">Mat: {aluno.registration}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{aluno.class_name}</td>
                      <td className="px-4 py-3 text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-white ${getNotaColor(aluno.media)}`}>
                          {aluno.media?.toFixed(1)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="secondary">{aluno.total_notas}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {aluno.notas?.slice(0, 5).map((n, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {n.tipo}: {n.valor}
                            </Badge>
                          ))}
                          {aluno.notas?.length > 5 && (
                            <Badge variant="secondary" className="text-xs">
                              +{aluno.notas.length - 5}
                            </Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Dialog de Gerenciamento de Notas */}
      <Dialog open={showGerenciar} onOpenChange={(open) => {
        setShowGerenciar(open);
        if (open) {
          setFilterTurmaGerenciar('all');
          loadProfessoresGerenciar();
        }
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-orange-600" />
              Gerenciar Notas por Professor
            </DialogTitle>
            <DialogDescription>
              Selecione os professores cujas notas você deseja remover do sistema.
              <span className="text-red-500 font-medium"> Esta ação é irreversível!</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-orange-50 border-orange-200">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-700">
                Use esta funcionalidade para remover notas de teste ou exemplo que foram cadastradas durante o desenvolvimento.
              </AlertDescription>
            </Alert>
            
            {/* Filtro por Turma */}
            <div className="flex items-center gap-2">
              <Label className="whitespace-nowrap">Filtrar por Turma:</Label>
              <Select 
                value={filterTurmaGerenciar} 
                onValueChange={(v) => {
                  setFilterTurmaGerenciar(v);
                  loadProfessoresGerenciar(v);
                  setProfessoresSelecionados([]);
                }}
              >
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Todas as Turmas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Turmas</SelectItem>
                  {turmas.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <ScrollArea className="h-[300px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Professor</TableHead>
                    <TableHead className="text-center">Notas</TableHead>
                    <TableHead>Disciplinas</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {professoresFiltrados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        Nenhum professor encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    professoresFiltrados.map((prof) => (
                      <TableRow key={prof.id} className={professoresSelecionados.includes(prof.id) ? 'bg-orange-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={professoresSelecionados.includes(prof.id)}
                            onCheckedChange={() => toggleProfessorSelecionado(prof.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{prof.nome}</p>
                            <p className="text-xs text-gray-500 font-mono">{prof.id}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{prof.total_notas}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {prof.disciplinas?.slice(0, 3).map((d, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{d}</Badge>
                            ))}
                            {prof.disciplinas?.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{prof.disciplinas.length - 3}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              if (window.confirm(`Tem certeza que deseja deletar todas as ${prof.total_notas} notas de ${prof.nome}?`)) {
                                deletarNotasProfessor(prof.id);
                              }
                            }}
                            disabled={deletando}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
            
            {professoresSelecionados.length > 0 && (
              <Alert className="bg-red-50 border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  {professoresSelecionados.length} professor(es) selecionado(s) para remoção.
                  Total de notas a serem deletadas: <strong>
                    {professoresFiltrados
                      .filter(p => professoresSelecionados.includes(p.id))
                      .reduce((acc, p) => acc + p.total_notas, 0)}
                  </strong>
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowGerenciar(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (window.confirm(`Tem certeza que deseja deletar as notas dos ${professoresSelecionados.length} professores selecionados?`)) {
                  deletarNotasSelecionadas();
                }
              }}
              disabled={deletando || professoresSelecionados.length === 0}
            >
              {deletando ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Deletar Selecionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de Limpeza de Dados de Exemplo */}
      <Dialog open={showLimpeza} onOpenChange={setShowLimpeza}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Limpar Dados de Exemplo
            </DialogTitle>
            <DialogDescription>
              Remove todas as notas e faltas de teste/exemplo do sistema.
              <span className="text-red-500 font-bold"> Esta ação é IRREVERSÍVEL!</span>
            </DialogDescription>
          </DialogHeader>
          
          {previewLimpeza ? (
            <div className="space-y-4">
              <Alert className="bg-red-50 border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  <strong>Serão removidos permanentemente:</strong>
                  <ul className="mt-2 list-disc list-inside">
                    <li>{previewLimpeza.resumo?.notas_a_remover || 0} notas</li>
                    <li>{previewLimpeza.resumo?.faltas_a_remover || 0} faltas</li>
                    <li className="font-bold">Total: {previewLimpeza.resumo?.total || 0} registros</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <div>
                <p className="font-medium text-sm mb-2">Critérios usados para identificar dados de exemplo:</p>
                <ul className="text-xs text-gray-600 list-disc list-inside">
                  {previewLimpeza.criterios_usados?.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
              
              {previewLimpeza.preview_notas?.length > 0 && (
                <div>
                  <p className="font-medium text-sm mb-2">Preview das notas a serem removidas:</p>
                  <ScrollArea className="h-[150px] border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Aluno</TableHead>
                          <TableHead>Disciplina</TableHead>
                          <TableHead>Teacher ID</TableHead>
                          <TableHead>Nota</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewLimpeza.preview_notas.map((n, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs">{n.student_name || n.student_id}</TableCell>
                            <TableCell className="text-xs">{n.subject_name || '-'}</TableCell>
                            <TableCell className="text-xs font-mono">{n.teacher_id}</TableCell>
                            <TableCell className="text-xs">{n.grade_value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}
              
              {previewLimpeza.resumo?.total === 0 && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    Nenhum dado de exemplo encontrado! Seu sistema está limpo.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Carregando preview...</span>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLimpeza(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={executarLimpeza}
              disabled={limpando || !previewLimpeza || previewLimpeza.resumo?.total === 0}
            >
              {limpando ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Confirmar Limpeza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Página Principal do DNACore
export default function DNACore() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [chartsData, setChartsData] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTurma, setFilterTurma] = useState('all');
  const [filterClassificacao, setFilterClassificacao] = useState('all');
  const [filterRisco, setFilterRisco] = useState('all');
  
  useEffect(() => {
    loadDashboard();
    loadStudents();
    loadTurmas();
    loadCharts();
  }, []);
  
  useEffect(() => {
    loadStudents();
  }, [filterTurma, filterClassificacao, filterRisco, searchTerm]);
  
  const loadDashboard = async () => {
    try {
      const res = await api.get('/dnacore/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  };
  
  const loadStudents = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterTurma !== 'all') params.turma_id = filterTurma;
      if (filterClassificacao !== 'all') params.classificacao = filterClassificacao;
      if (filterRisco === 'alto') params.risco = 'alto';
      if (searchTerm) params.search = searchTerm;
      
      const res = await api.get('/dnacore/students', { params });
      setStudents(res.data.students || []);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTurmas = async () => {
    try {
      const res = await api.get('/class-groups');
      setTurmas(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };
  
  const loadCharts = async () => {
    try {
      const res = await api.get('/dnacore/charts/turmas');
      setChartsData(res.data);
    } catch (error) {
      console.error('Erro ao carregar gráficos:', error);
    }
  };
  
  const handleRecalculateAll = async () => {
    setRecalculating(true);
    try {
      const res = await api.post('/dnacore/recalculate-all');
      toast.success(`Scores recalculados para ${res.data.processados} alunos`);
      loadDashboard();
      loadStudents();
      loadCharts();
    } catch (error) {
      toast.error('Erro ao recalcular scores');
    } finally {
      setRecalculating(false);
    }
  };
  
  const openStudentModal = (student) => {
    setSelectedStudent(student);
    setModalOpen(true);
  };
  
  const getStatusColor = (classificacao) => {
    switch (classificacao) {
      case 'excelente': return 'bg-green-500';
      case 'estavel': return 'bg-blue-500';
      case 'atencao': return 'bg-yellow-500';
      case 'critico': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="dnacore-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-600 to-purple-800 text-white">
            <Brain className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">DNACore</h1>
            <p className="text-gray-500">Núcleo Pedagógico com Inteligência Artificial</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadDashboard}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button 
            onClick={handleRecalculateAll} 
            disabled={recalculating}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
          >
            {recalculating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Recalcular Todos os Scores
          </Button>
        </div>
      </div>
      
      {/* Tabs Principais */}
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-6 bg-white border shadow-sm">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="notas" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
            <BookOpen className="w-4 h-4 mr-2" />
            Notas Globais
          </TabsTrigger>
          <TabsTrigger value="alunos" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
            <Users className="w-4 h-4 mr-2" />
            Painel de Alunos
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Dashboard */}
        <TabsContent value="dashboard">
          {/* Cards de Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
        <MetricCard
          title="Score Médio da Escola"
          value={dashboard?.saude_pedagogica?.score_medio?.toFixed(1) || '0'}
          subtitle="de 100 pontos"
          icon={Award}
          color="purple"
        />
        <MetricCard
          title="Total de Alunos"
          value={dashboard?.saude_pedagogica?.total_alunos || 0}
          subtitle="matriculados"
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="Alunos em Risco"
          value={dashboard?.metricas?.alunos_risco || 0}
          subtitle="precisam de atenção"
          icon={AlertTriangle}
          color="red"
        />
        <MetricCard
          title="Total de Faltas"
          value={dashboard?.metricas?.total_faltas || 0}
          subtitle="no período"
          icon={UserX}
          color="orange"
        />
        <MetricCard
          title="Ocorrências"
          value={dashboard?.metricas?.total_ocorrencias || 0}
          subtitle="registradas"
          icon={AlertCircle}
          color="yellow"
        />
        <MetricCard
          title="Alertas Ativos"
          value={dashboard?.alertas_ativos || 0}
          subtitle="pendentes"
          icon={Bell}
          color="red"
        />
      </div>
      
      {/* Distribuição de Classificações */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Distribuição de Classificações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Excelente', key: 'excelente', color: 'green', icon: Award },
                { label: 'Estável', key: 'estavel', color: 'blue', icon: CheckCircle },
                { label: 'Atenção', key: 'atencao', color: 'yellow', icon: AlertCircle },
                { label: 'Crítico', key: 'critico', color: 'red', icon: XCircle }
              ].map((item) => (
                <div key={item.key} className={`p-4 rounded-xl bg-${item.color}-50 border border-${item.color}-200`}>
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className={`w-5 h-5 text-${item.color}-600`} />
                    <span className={`font-medium text-${item.color}-700`}>{item.label}</span>
                  </div>
                  <div className={`text-3xl font-bold text-${item.color}-600`}>
                    {dashboard?.saude_pedagogica?.distribuicao?.[item.key] || 0}
                  </div>
                  <div className="text-sm text-gray-500">
                    {dashboard?.saude_pedagogica?.percentuais?.[item.key]?.toFixed(1) || 0}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Ranking de Turmas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-purple-600" />
              Ranking de Turmas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {chartsData?.turmas?.slice(0, 8).map((turma, idx) => (
                  <div key={turma.turma_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-300 text-orange-900' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium truncate max-w-[120px]">{turma.turma}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">{turma.media}</span>
                      <div className={`w-2 h-2 rounded-full ${
                        turma.media >= 70 ? 'bg-green-500' :
                        turma.media >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
        </TabsContent>
        
        {/* Tab Notas Globais */}
        <TabsContent value="notas">
          <NotasGlobaisPanel turmas={turmas} />
        </TabsContent>
        
        {/* Tab Painel de Alunos */}
        <TabsContent value="alunos">
      
      {/* Filtros e Tabela */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Painel Global de Alunos
            </CardTitle>
          </div>
          <CardDescription>
            Visualize e gerencie todos os alunos da escola
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar aluno por nome ou matrícula..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-student"
                />
              </div>
            </div>
            
            <Select value={filterTurma} onValueChange={setFilterTurma}>
              <SelectTrigger className="w-[200px]" data-testid="filter-turma">
                <SelectValue placeholder="Filtrar por turma" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as turmas</SelectItem>
                {turmas.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterClassificacao} onValueChange={setFilterClassificacao}>
              <SelectTrigger className="w-[180px]" data-testid="filter-classificacao">
                <SelectValue placeholder="Classificação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="excelente">Excelente</SelectItem>
                <SelectItem value="estavel">Estável</SelectItem>
                <SelectItem value="atencao">Atenção</SelectItem>
                <SelectItem value="critico">Crítico</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterRisco} onValueChange={setFilterRisco}>
              <SelectTrigger className="w-[180px]" data-testid="filter-risco">
                <SelectValue placeholder="Risco" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="alto">Alto Risco</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Tabela */}
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Aluno</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Turma</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Score</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Média</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Frequência</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Faltas</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Ocorrências</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={9} className="px-4 py-3">
                        <Skeleton className="h-10 w-full" />
                      </td>
                    </tr>
                  ))
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                      Nenhum aluno encontrado com os filtros selecionados
                    </td>
                  </tr>
                ) : (
                  students.map((student) => (
                    <tr 
                      key={student.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => openStudentModal(student)}
                      data-testid={`student-row-${student.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${getStatusColor(student.classificacao)} bg-opacity-20 flex items-center justify-center text-sm font-bold`}>
                            {student.name?.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{student.name}</div>
                            <div className="text-xs text-gray-500">Mat: {student.registration}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{student.class_name}</td>
                      <td className="px-4 py-3 text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-white ${
                          student.score_total >= 80 ? 'bg-green-500' :
                          student.score_total >= 60 ? 'bg-blue-500' :
                          student.score_total >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}>
                          {Math.round(student.score_total)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold">{student.media?.toFixed(1)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={student.frequencia_percent} className="w-16 h-2" />
                          <span className="text-sm text-gray-600">{student.frequencia_percent?.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={student.faltas > 5 ? 'destructive' : 'secondary'}>
                          {student.faltas}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={student.ocorrencias > 0 ? 'destructive' : 'secondary'}>
                          {student.ocorrencias}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className={`w-4 h-4 rounded-full mx-auto ${getStatusColor(student.classificacao)}`} 
                             title={student.classificacao} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); openStudentModal(student); }}
                          className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal do Aluno */}
      <StudentModal 
        student={selectedStudent} 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
      />
    </div>
  );
}
